import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';
import { clearAllDrafts } from '../utils/historiaDraft';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      const scoped = localStorage.getItem('whatsappScoped') === 'true';

      if (token && savedUser) {
        // Sesión limitada de WhatsApp: no validar contra /auth/me (el token no
        // sirve para la API REST). La página de acceso re-verifica el magic link.
        if (scoped) {
          setUser(JSON.parse(savedUser));
          connectSocket();
        } else {
          try {
            setUser(JSON.parse(savedUser));
            await api.get('/auth/me');
            connectSocket();
          } catch (error) {
            console.error('Error loading user:', error);
            // Sesión caducada al recargar, no un cierre de sesión del usuario:
            // se conservan los borradores de historia para poder recuperarlos.
            logout({ conservarBorradores: true });
          }
        }
      }
      setLoading(false);
    };

    loadUser();
    return () => { disconnectSocket(); };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, refreshToken, user } = response.data.data;
      localStorage.setItem('token', token);
      // Sesión larga: el access token dura 15 min y se renueva con este.
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      connectSocket();
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al iniciar sesión',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token, refreshToken, user } = response.data.data;
      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      connectSocket();
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al registrarse',
      };
    }
  };

  // Sesión LIMITADA emitida por el magic link de WhatsApp. Sólo habilita el
  // socket de una cita puntual; el token no sirve para la API REST protegida.
  const loginWithScopedSession = (sessionToken, scopedUser) => {
    const userWithFlag = { ...scopedUser, scoped: true };
    localStorage.setItem('token', sessionToken);
    localStorage.setItem('user', JSON.stringify(userWithFlag));
    localStorage.setItem('whatsappScoped', 'true');
    setUser(userWithFlag);
    connectSocket();
    return userWithFlag;
  };

  // Se usa también como onClick={logout}: el primer argumento puede ser el
  // evento del clic, por eso se exige `true` explícito para conservar borradores.
  const logout = (opciones) => {
    const conservarBorradores = opciones?.conservarBorradores === true;
    // Se avisa al backend para que revoque el refresh token: sin esto el token
    // seguiría vivo hasta expirar aunque el usuario haya cerrado sesión.
    // Va sin await a propósito — cerrar sesión no debe quedarse esperando a la
    // red — y el .catch() evita que un fallo deje al usuario dentro.
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => {});
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('whatsappScoped');
    // Los borradores de historia clínica son datos clínicos en texto plano: al
    // cerrar sesión no deben quedarse en el navegador (puede ser compartido).
    if (!conservarBorradores) clearAllDrafts();
    setUser(null);
    disconnectSocket();
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithScopedSession,
    isAuthenticated: !!user,
    isScopedSession: user?.scoped === true,
    isDoctor:  user?.role === 'doctor',
    isPatient: user?.role === 'patient',
    isAdmin:   user?.role === 'admin', // <-- ITER-0
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
